import { FC, useCallback, useMemo, useState } from "react";
import { Box, Button, Chip, Divider, IconButton, MenuItem, styled, Typography, useTheme } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays, faChevronDown, faChevronUp } from "@fortawesome/duotone-light-svg-icons";
import { Checkbox, InputOnlyField, IValidationError, Markdown, Multiselect, Numeric, Select, Text } from "@ngt/forms";
import ConfigurationFormEditPanel, { ConfigurationBox } from './ConfigurationFormEditPanel';
import { ConfigurationType, IConfigurationEvent } from "@/utilities/forms/ConfigurationFormProcessor";
import { Condition, ConditionMode, IFormState, useFormActions, useScopedField } from '@ngt/forms-core';
import { IEventMetadata, ISourceDataConfigurationEvent, ISourceDataConfigurationForm, NoteStatus, NoteType } from '@/api/dtos';
import { IConfigurationFormEditContext } from "@/contexts/forms/ConfigurationFormEditContext";
import ConfigurationFormEditInfo, { ConfigurationDividerBox, ConfigurationFlexBox, ConfigurationTitle, OtherVersionsBox, OtherVersionsItem } from './ConfigurationFormEditInfo';
import { get } from "lodash-es";
import ConfigurationFormEditCopy from "./ConfigurationFormEditCopy";
import ConfigurationFormEditUpdate from "./ConfigurationFormEditUpdate";
import { SourceDataPriority } from '../../../../api/dtos';
import { faCaretDown, faCaretUp, faExclamationTriangle, faTriangle } from "@fortawesome/duotone-light-svg-icons";

export interface IConfigurationFormEditEventProps {
    context: IConfigurationFormEditContext<IConfigurationEvent>
}

interface IOtherVersion {
    type: ConfigurationType;
    eventKey?: string;
    eventMetadata?: IEventMetadata;
    eventConfiguration: ISourceDataConfigurationEvent;
    configurationPropertyPath: string;
    linked: boolean;
    isGlobal: boolean;
    isTrial: boolean;
}

const onConditionNotMet = () => {};

const ConfigurationFormEditEvent: FC<IConfigurationFormEditEventProps> = ({
    context
}) => {
    const {
        configurationPropertyPath,
        item: {
            eventMetadata
        }
    } = context;
    
    const [openOtherVersions, setOpenOtherVersions] = useState(false);

    const actions = useFormActions();

    const theme = useTheme();

    const allVersions = useMemo(() => {
        if (context.usingTrialConfiguration) {
            return null;
        }

        let currentVersion: IOtherVersion | undefined = undefined;

        const versions: IOtherVersion[] = [];

        const globalPropertyPath = `configuration.events.${context.item.eventKey!}`;
        const globalFormVersion = actions.getFieldValue<ISourceDataConfigurationEvent>(globalPropertyPath);
        
        if (globalFormVersion /*&& !globalFormVersion?.linked*/) {
            const data = {
                type: ConfigurationType.Event,
                eventKey: context.item.eventKey,
                eventMetadata: context.item.eventMetadata,
                eventConfiguration: globalFormVersion,
                configurationPropertyPath: globalPropertyPath,
                linked: !!globalFormVersion?.linked,
                isGlobal: !context.item.isTrialConfiguration,
                isTrial: !!context.item.isTrialConfiguration
            }

            if (context.configurationPropertyPath !== globalPropertyPath) {
                versions.push(data)
            }
            else {
                currentVersion = data;
            }
        }

        const trialPropertyPath = `trialConfiguration.events.${context.item.eventKey!}`
        const trialFormVersion = actions.getFieldValue<ISourceDataConfigurationEvent>(trialPropertyPath);
        
        if (trialFormVersion /*&& !trialFormVersion?.linked*/) {
            const data = {
                type: ConfigurationType.Event,
                eventKey: context.item.eventKey,
                eventMetadata: context.item.eventMetadata,
                eventConfiguration: trialFormVersion,
                configurationPropertyPath: trialPropertyPath,
                linked: !!trialFormVersion?.linked,
                isGlobal: false,
                isTrial: true
            }

            if (context.configurationPropertyPath !== trialPropertyPath) {
                versions.push(data)
            }
            else {
                currentVersion = data;
            }
        }


        

        return {
            otherVersions: versions.sort((a, b) => {
                
                if (!a.isTrial && b.isTrial) {
                    return -1;
                }
                else if (a.isTrial && !b.isTrial) {
                    return 1;
                }

                if (!a.isGlobal && b.isGlobal) {
                    return -1;
                }
                else if (a.isGlobal && !b.isGlobal) {
                    return 1;
                }


                if (a.eventKey !== b.eventKey) {
                    return (a.eventKey ?? 'zzz') < (b.eventKey ?? 'zzz') ? -1 : 1;
                }

                return -1;
            }),
            currentVersion
        };
    }, [context.item, context.usingTrialConfiguration, context.usingGlobalConfiguration, actions])

    const otherVersionContent = useMemo(() => {

        if (!allVersions || !allVersions.otherVersions) {
            return null;
        }

        return (
            <OtherVersionsBox>
                {
                    allVersions.otherVersions.map((x, i, a) => {            
                        return (
                            <OtherVersionsItem key={x.configurationPropertyPath}>
                                <ConfigurationFormEditInfo 
                                    {...x}
                                    dividers
                                />
                                <ConfigurationDividerBox>
                                    <Divider />
                                </ConfigurationDividerBox>
                                <ConfigurationFlexBox sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    {
                                        !!allVersions.currentVersion && (
                                            <>
                                                <ConfigurationFormEditCopy 
                                                    target={allVersions.currentVersion}
                                                    source={x}
                                                />
                                                <ConfigurationFormEditUpdate 
                                                    target={x}
                                                    source={allVersions.currentVersion}
                                                />
                                            </>
                                        )
                                    }
                                </ConfigurationFlexBox>
                            </OtherVersionsItem>
                        )
                    })
                }
            </OtherVersionsBox>
        )
    }, [allVersions, context.item]);

    const showNoteCondition = useCallback((state: IFormState<any, IValidationError>, parentPropertyPath?: string | null) => {
        const showNotes = get(state.value, `${configurationPropertyPath}.showNotes`);

        return !!showNotes;
    }, [configurationPropertyPath])

    return (
        <ConfigurationFormEditPanel 
            context={context} 
            type="Event"
            typeIcon={faCalendarDays}
        >
            {
                allVersions?.otherVersions && (
                    <>
                        <ConfigurationTitle 
                            variant="h2" 
                            {...{component: Button} as any} 
                            color='inherit' 
                            sx={{ 
                                display: 'flex', 
                                padding: 0,
                                textTransform: 'none'
                            }}
                            onClick={() => setOpenOtherVersions(c => !c)}
                        >
                            <Typography color={theme.palette.secondary.main} variant="h2">
                                Other Versions
                            </Typography>
                            <Chip 
                                label={allVersions.otherVersions.length}
                                color="primary"
                                sx={{
                                    ml: 'auto'
                                }}
                                size="small"
                            />
                            <IconButton component="div">
                                <FontAwesomeIcon icon={openOtherVersions ? faChevronUp : faChevronDown} />
                            </IconButton>
                        </ConfigurationTitle>
                        {
                            openOtherVersions && (
                                otherVersionContent
                            )
                        }
                    </>
                )
            }
            <ConfigurationTitle color={theme.palette.secondary.main} variant="h2">
                Options
            </ConfigurationTitle>
            <ConfigurationBox>
                <InputOnlyField 
                    label="Override Name"
                    propertyPath={`${configurationPropertyPath}.name`}
                    component={Text}
                    margin="normal"
                />
                <InputOnlyField 
                    label="Priority"
                    propertyPath={`${configurationPropertyPath}.priority`}
                    component={Select}
                    FormControlProps={{
                        margin: 'normal'
                    }}
                >
                    <MenuItem
                        value={SourceDataPriority.Low}
                    >
                        <Box sx={{display: 'flex', width: 'auto', flex: '1 1 auto'}}>
                            Low 
                            <Box sx={{ml: 'auto', color: 'success.main', transform: 'rotate(180deg)'}}>
                                <FontAwesomeIcon icon={faTriangle} />
                            </Box>
                        </Box>
                    </MenuItem>
                    <MenuItem
                        value={SourceDataPriority.Normal}
                    >
                        Normal
                    </MenuItem>
                    <MenuItem
                        value={SourceDataPriority.High}
                    >
                        <Box sx={{display: 'flex', width: 'auto', flex: '1 1 auto'}}>
                            High
                            <Box sx={{ml: 'auto', color: 'warning.main'}}>
                                <FontAwesomeIcon icon={faTriangle} />
                            </Box>
                        </Box>
                    </MenuItem>
                    <MenuItem
                        value={SourceDataPriority.Critical}
                    >
                        <Box sx={{display: 'flex', width: 'auto', flex: '1 1 auto'}}>
                            Critical
                            <Box sx={{ml: 'auto', color: 'error.main'}}>
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                            </Box>
                        </Box>
                    </MenuItem>
                </InputOnlyField>
                <InputOnlyField 
                    label="Hide Event When No Data Provided"
                    propertyPath={`${configurationPropertyPath}.excludeBlank`}
                    component={Checkbox}
                    FormControlProps={{
                        margin: 'normal'
                    }}
                />
            </ConfigurationBox>
            
            {
                eventMetadata?.repeating && (
                    <>
                        <ConfigurationTitle color={theme.palette.secondary.main} variant="h4">
                            Collection
                        </ConfigurationTitle>
                        <ConfigurationBox>
                            <InputOnlyField
                                label="Lastest Repeat Only"
                                propertyPath={`${configurationPropertyPath}.latestRepeat`}
                                component={Checkbox}
                                FormControlProps={{
                                    margin: 'normal'
                                }}
                            />
                            <InputOnlyField
                                label="Minimum Repeat"
                                propertyPath={`${configurationPropertyPath}.minimumRepeat`}
                                component={Numeric}
                                margin="normal"
                            />
                            <InputOnlyField
                                label="Maximum Repeat"
                                propertyPath={`${configurationPropertyPath}.maximumRepeat`}
                                component={Numeric}
                                margin="normal"
                            />
                        </ConfigurationBox>
                    </>
                )
            }

            <ConfigurationTitle color={theme.palette.secondary.main} variant="h2">
                Alerts
            </ConfigurationTitle>
            <ConfigurationBox>
                <InputOnlyField 
                    label="Information"
                    propertyPath={`${configurationPropertyPath}.alertInformation`}
                    component={Markdown}
                    margin="normal"
                    //color="info"
                />
                <InputOnlyField 
                    label="Warning"
                    propertyPath={`${configurationPropertyPath}.alertWarning`}
                    component={Markdown}
                    margin="normal"
                    //color="warning"
                />
                <InputOnlyField 
                    label="Error"
                    propertyPath={`${configurationPropertyPath}.alertError`}
                    component={Markdown}
                    margin="normal"
                    //color="error"
                />
                <InputOnlyField 
                    label="Success"
                    propertyPath={`${configurationPropertyPath}.alertSuccess`}
                    component={Markdown}
                    margin="normal"
                    //color="success"
                />
            </ConfigurationBox>
            
            <ConfigurationTitle variant="h2" color={theme.palette.secondary.main}>
                Discrepancy Notes
            </ConfigurationTitle>
            <ConfigurationBox>
                <InputOnlyField 
                    label="Show Discrepancy Notes"
                    propertyPath={`${configurationPropertyPath}.showNotes`}
                    component={Checkbox}
                    FormControlProps={{
                        margin: 'normal'
                    }}
                />
                <Condition 
                    mode={context.usingTrialConfiguration ? ConditionMode.Enable : ConditionMode.Show}
                    subscription={{ value: true }}
                    condition={showNoteCondition}
                    onConditionNotMet={onConditionNotMet}
                >
                    <InputOnlyField 
                        label="Types"
                        propertyPath={`${configurationPropertyPath}.noteTypes`}
                        component={Multiselect}
                        FormControlProps={{
                            margin: 'normal'
                        }}
                    >
                        <MenuItem
                            value={NoteType.ValidationFailed}
                        >
                            Validation Failed
                        </MenuItem>
                        <MenuItem
                            value={NoteType.Annotation}
                        >
                            Annotation
                        </MenuItem>
                        <MenuItem
                            value={NoteType.Query}
                        >
                            Query
                        </MenuItem>
                        <MenuItem
                            value={NoteType.ReasonForChange}
                        >
                            Reason For Change
                        </MenuItem>
                        <MenuItem
                            value={NoteType.Internal}
                        >
                            Internal
                        </MenuItem>
                        <MenuItem
                            value={NoteType.ProtocolDeviation}
                        >
                            Protocol Deviation
                        </MenuItem>
                        <MenuItem
                            value={NoteType.ImportantProtocolDeviation}
                        >
                            Important Protocol Deviation
                        </MenuItem>
                    </InputOnlyField>
                    <InputOnlyField 
                        label="Statuses"
                        propertyPath={`${configurationPropertyPath}.noteStatuses`}
                        component={Multiselect}
                        FormControlProps={{
                            margin: 'normal'
                        }}
                    >
                        <MenuItem
                            value={NoteStatus.New}
                        >
                            New
                        </MenuItem>
                        <MenuItem
                            value={NoteStatus.Updated}
                        >
                            Updated
                        </MenuItem>
                        <MenuItem
                            value={NoteStatus.ResolutionProposed}
                        >
                            Resolution Proposed
                        </MenuItem>
                        <MenuItem
                            value={NoteStatus.Closed}
                        >
                            Closed
                        </MenuItem>
                        <MenuItem
                            value={NoteStatus.NotApplicable}
                        >
                            Not Applicable
                        </MenuItem>
                    </InputOnlyField>
                </Condition>
            </ConfigurationBox>
        </ConfigurationFormEditPanel>
    )
}

export default ConfigurationFormEditEvent;